import React, { useState } from 'react';
import './ExpiryNotificationBanner.css';

/**
 * ExpiryNotificationBanner - Displays document expiry notifications
 * @param {Array} notifications - Array of notification objects
 * @param {Function} onDismiss - Callback when user dismisses a notification
 * @param {Function} onUpdate - Callback when user clicks "Update Now"
 * @param {Function} onClose - Callback to close the modal
 */
const ExpiryNotificationBanner = ({ notifications, onDismiss, onUpdate, onClose }) => {
    if (!notifications || notifications.length === 0) {
        return null;
    }

    const getUrgencyClass = (notification) => {
        const expiryDate = new Date(notification.expiry_date);
        const now = new Date();
        const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

        if (daysRemaining < 0) return 'overdue';
        if (daysRemaining <= 7) return 'urgent';
        return 'warning';
    };

    const getUrgencyLabel = (notification) => {
        const expiryDate = new Date(notification.expiry_date);
        const now = new Date();
        const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

        if (daysRemaining < 0) return `${Math.abs(daysRemaining)} days overdue`;
        if (daysRemaining === 0) return 'Expires today';
        if (daysRemaining === 1) return 'Expires tomorrow';
        return `${daysRemaining} days left`;
    };

    return (
        <div className="expiry-notification-overlay" onClick={onClose}>
            <div
                className="expiry-notification-container"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="expiry-notification-header">
                    <h2>Document Alerts</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="expiry-notification-list">
                    {notifications.map((notification) => {
                        const urgencyClass = getUrgencyClass(notification);
                        return (
                            <div
                                key={notification.notification_id}
                                className={`expiry-notification-card ${urgencyClass}`}
                            >
                                <div className="notification-content">
                                    <p className="notification-message">{notification.message}</p>
                                    <div className="notification-meta">
                                        <span className={`notification-badge ${urgencyClass}`}>
                                            {getUrgencyLabel(notification)}
                                        </span>
                                        <div className="notification-actions">
                                            <button
                                                className="dismiss-btn"
                                                onClick={() => onDismiss(notification.notification_id)}
                                            >
                                                Remind Later
                                            </button>
                                            <button
                                                className="update-btn"
                                                onClick={() => onUpdate(notification)}
                                            >
                                                Update Now
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

/**
 * FloatingExpiryBadge - A small badge that shows notification count
 * @param {number} count - Number of expiry notifications
 * @param {Function} onClick - Callback when badge is clicked
 */
export const FloatingExpiryBadge = ({ count, onClick }) => {
    if (!count || count === 0) return null;

    return (
        <div className="expiry-badge-floating" onClick={onClick}>
            <span className="expiry-badge-count">{count}</span>
            <span>Document Alert{count > 1 ? 's' : ''}</span>
        </div>
    );
};

export default ExpiryNotificationBanner;
